import React, { ReactNode } from 'react'
import { observer } from 'mobx-react'
import cx from 'classnames'
import DocumentsListViewModel from './DocumentsListViewModel'
import styles from './DocumentsList.sass'
import {
  Table,
  TableHead,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
} from 'components/Table'
import { Id } from 'types/definitions'
import Document from 'documents/Document'
import FileTypeIcon from 'components/FileTypeIcon'
import { IconType } from 'utils/icon-util'
import Avatar from 'components/Avatar'
import RadialProgress from 'components/RadialProgress'
import Icon from 'components/Icon'
import Spinner from 'components/Spinner'
import { Dropdown, DropdownItem, DropdownTitle } from 'components/Dropdown'
import { IconButton } from 'components/IconButton'
import DocumentListHeader from './DocumentListHeader'
import InfiniteScrollLoader from 'components/InfiniteScrollLoader'
import InfiniteScrollProvider from 'utils/InfiniteScrollProvider'
import Tooltip from 'components/Tooltip'
import { ViewDocumentDetailsDelegate } from '../DocumentDetailsRootViewModel'
import Pill from 'components/Pill'
import ConfirmSheet from 'components/ConfirmSheet'
import {
  ModalSheetContentBlock,
  ModalSheetContentDescription,
  ModalSheetContentTitle,
} from 'components/ModalSheet'
import { DialogIllustration } from 'components/Dialog'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  children: ReactNode
  header: ReactNode
}

export default observer(function DocumentsListContainer({
  children,
  header,
}: Props) {
  return (
    <div className={cx(styles.root)}>
      <div className={styles.container}>
        <div className={styles.headerContainer}>{header}</div>
        <div className={styles.box}>{children}</div>
      </div>
    </div>
  )
})

interface DocumentsListProps extends React.HTMLAttributes<HTMLDivElement> {
  vm: DocumentsListViewModel
  scrollProvider: InfiniteScrollProvider<Document, Record<string, unknown>>
  onQueueFilesUpload: (files: FileList) => void
  onViewDocumentDetails: ViewDocumentDetailsDelegate
  canUploadFiles: boolean
}

export const DocumentsList = observer(function DocumentsList({
  vm,
  scrollProvider,
  onQueueFilesUpload,
  onViewDocumentDetails,
  canUploadFiles,
}: DocumentsListProps) {
  return (
    <>
      <div className={styles.actions}>
        <DocumentListHeader
          showUploadAction={canUploadFiles}
          vm={vm}
          onQueueFilesUpload={onQueueFilesUpload}
        />
      </div>
      <div className={styles.table}>
        <DocumentsTable onViewDocumentDetails={onViewDocumentDetails} vm={vm} />
        <InfiniteScrollLoader scrollProvider={scrollProvider} />
      </div>
      <ConfirmSheet
        yes="Delete Forever"
        danger
        state={vm.confirmDocumentDelete}
      >
        <ModalSheetContentBlock>
          <DialogIllustration
            src={require('assets/images/img_remove-collaborator.svg')}
            alt="Illustration of a women holding a phone with a crossed-out bubble above the phone."
          />
          <ModalSheetContentTitle>
            Are you sure you want to delete this file?
          </ModalSheetContentTitle>
          <ModalSheetContentDescription>
            This action cannot be undone. Are you sure that you want to
            permanently delete this document?
          </ModalSheetContentDescription>
        </ModalSheetContentBlock>
      </ConfirmSheet>
    </>
  )
})

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'long',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
})

interface DocumentsTableProps extends React.HTMLAttributes<HTMLDivElement> {
  vm: DocumentsListViewModel
  onViewDocumentDetails: ViewDocumentDetailsDelegate
}
const DocumentsTable = observer(function ({
  vm,
  onViewDocumentDetails,
}: DocumentsTableProps) {
  const { filteredDocuments, currentSort, currentSortOrder, setSort } = vm

  return (
    <Table striped stickyHeader>
      <TableHead>
        {vm.columns.map((col) => (
          <TableHeader
            key={col.key}
            canSort={col.sortable}
            sortOrder={col.key === currentSort ? currentSortOrder : 'none'}
            onSort={(newSort) => setSort(col.key, newSort)}
            align={'left'}
          >
            {col.header}
          </TableHeader>
        ))}
        <TableHeader></TableHeader>
      </TableHead>
      <TableBody>
        {filteredDocuments.map((document) => {
          return (
            <TableRow
              className={styles.documentRow}
              key={Id.assert(document.longId)}
            >
              <TableCell>
                <DocumentItem
                  document={document}
                  onView={() => onViewDocumentDetails(document)}
                />
              </TableCell>
              {vm.isColumnHeaderVisible('workspace') && (
                <TableCell width={200}>{document.workspace.name}</TableCell>
              )}
              <TableCell width={125} className={styles.muted}>
                {document.fileType}
              </TableCell>
              <TableCell width={220}>
                {dateFormatter.format(document.dateModified)}
              </TableCell>
              <TableCell align="right" width={20}>
                <ItemIcon
                  document={document}
                  onDownload={() => vm.download(document)}
                  onView={() => onViewDocumentDetails(document)}
                  onDelete={() => vm.delete(document)}
                />
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
})

interface DocumentItemProps extends React.HTMLAttributes<HTMLDivElement> {
  document: Document
  onView?: () => void
}
const DocumentItem = observer(function ({
  document,
  onView = () => undefined,
}: DocumentItemProps) {
  const icon = IconType(
    (document.latestVersion && document.latestVersion.files[0].fileName) || '',
  )

  const { tags } = document

  const isDamaged = !document.latestVersion && !document.uploadProgress

  return (
    <div
      className={styles.documentItem}
      data-test={'document-list-item-' + document.title}
    >
      <div className={styles.icon}>
        <FileTypeIcon
          height={36}
          primaryColor={icon.color}
          strokeColor="#bcbcbe"
          type={icon.type}
        />
      </div>
      <div className={styles.info}>
        {document.archived && (
          <div className={styles.archived}>
            <Pill color="red" shape="square" trim>
              Archived
            </Pill>
          </div>
        )}
        <div className={styles.title}>
          {isDamaged ? (
            <span>{document.title}</span>
          ) : (
            <a
              className={styles.titleLink}
              href="#"
              onClick={(e) => {
                e.preventDefault()
                onView()
              }}
            >
              {document.title}
            </a>
          )}
          {document.isPurged && <span className={styles.purged}>Purged</span>}
          {isDamaged && (
            <Tooltip content={'Something went wrong with this document.'}>
              <span className={styles.purged}>Corrupted</span>
            </Tooltip>
          )}
        </div>
        <div className={styles.meta}>
          <div className={styles.metaItem}>
            <Avatar
              size={20}
              subject={
                document.latestVersionUnpurged
                  ? document.latestVersionUnpurged.author
                  : document.owner
              }
            />
          </div>
          {tags.length > 0 && (
            <div className={styles.metaItem}>
              <div className={cx(styles.tags)}>
                {tags.length > 3 ? (
                  <Tooltip content={tags.map((x) => x.label).join(', ')}>
                    <div className={styles.tagCount}>{tags.length} tags</div>
                  </Tooltip>
                ) : (
                  tags.map((tag) => (
                    <div key={Id.assert(tag.id)} className={styles.tag}>
                      {tag.label}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
})

interface ItemIconProps {
  document: Document
  onDownload: () => void
  onView: () => void
  onDelete: () => void
}

const ItemIcon = observer(function ItemIcon({
  document,
  onDownload,
  onView,
  onDelete,
}: ItemIconProps) {
  if (document.uploadProgress) {
    return (
      <RadialProgress size={24} progress={document.uploadProgress.percent} />
    )
  }

  const isWaitingForScan =
    document.latestVersion && document.latestVersion.state === 'Pending'

  const isScanning =
    document.latestVersion && document.latestVersion.state === 'Scanning'

  const isInfected =
    document.latestVersion && document.latestVersion.state === 'Purged'

  const isDownloadable =
    document.latestVersion && document.latestVersion.downloadable

  const hasFailed =
    document.latestVersion && document.latestVersion.state === 'Failed'

  if (isWaitingForScan) {
    return (
      <span
        className={styles.status}
        title="This document is waiting for a virus scan to complete"
      >
        <Icon name="pending" />
      </span>
    )
  }

  if (isScanning) {
    return (
      <span
        className={styles.status}
        title="This document is currently undergoing a virus scan"
      >
        <Spinner />
      </span>
    )
  }

  if (hasFailed) {
    return (
      <span
        className={cx(styles.status, styles.failed)}
        title="This document has failed to upload"
      >
        <Icon name="info" />
      </span>
    )
  }

  if (isInfected) {
    return (
      <span
        className={cx(styles.status, styles.virus)}
        title="This document is infected with a virus and is not available for download"
      >
        <Icon name="virus" />
      </span>
    )
  }

  return (
    <span className={styles.status}>
      <Dropdown
        autoDismiss
        position="bottom-end"
        button={
          <IconButton data-test={'download-actions-dropdown-' + document.title}>
            <Icon name="more" />
          </IconButton>
        }
      >
        <DropdownTitle>Actions</DropdownTitle>
        {isDownloadable && (
          <>
            <DropdownItem
              icon={<Icon name="arrow"></Icon>}
              onClick={() => onView()}
            >
              View Details
            </DropdownItem>
            <DropdownItem
              icon={<Icon name="download"></Icon>}
              onClick={() => onDownload()}
            >
              Download
            </DropdownItem>
          </>
        )}
        <DropdownItem
          icon={<Icon name="delete"></Icon>}
          onClick={() => onDelete()}
        >
          Delete
        </DropdownItem>
      </Dropdown>
    </span>
  )
})
